import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import TopNav, { AppNavMode } from "../components/TopNav.tsx";
import Forbidden from "./Forbidden.tsx";
import { AdminSortDir, AdminSortField, AdminUserRow, listUsers } from "../services/admin.ts";

const PAGE_SIZE = 5;

const formatDate = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString() : "N/A");

// Mirrors get_current_active_subscriber: active needs the flag AND an unexpired end date.
const isEffectivelyActive = (u: AdminUserRow) =>
  u.subscription_active && (!u.subscription_ends_at || new Date(u.subscription_ends_at) > new Date());

const ADMIN_ACTIONS = ["Reset password", "Extend subscription", "Deactivate"];

const AdminUsers: React.FC = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<AdminUserRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState<AdminSortField>("created_at");
  const [sortDir, setSortDir] = useState<AdminSortDir>("desc");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [forbidden, setForbidden] = useState(false);
  const [notice, setNotice] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await listUsers({ page, pageSize: PAGE_SIZE, sortBy, sortDir, q: search });
      setRows(data.items);
      setTotal(data.total);
    } catch (err: any) {
      if (err.response?.status === 403) setForbidden(true);
      else setError(err.response?.data?.detail || "Failed to load users");
    } finally {
      setLoading(false);
    }
  }, [page, sortBy, sortDir, search]);

  useEffect(() => {
    load();
  }, [load]);

  const handleModeNav = (mode: AppNavMode) => {
    if (mode === "admin") return;
    if (mode === "practice-exam") navigate("/practice-exam");
    else navigate(`/questions?mode=${mode}`);
  };

  const toggleSort = (field: AdminSortField) => {
    if (field === sortBy) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortBy(field);
      setSortDir("asc");
    }
    setPage(1);
  };

  const sortIndicator = (field: AdminSortField) => (field === sortBy ? (sortDir === "asc" ? " ▲" : " ▼") : "");
  const ariaSort = (field: AdminSortField) =>
    field === sortBy ? (sortDir === "asc" ? "ascending" : "descending") : "none";

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  if (forbidden) return <Forbidden />;

  const sortableHeader = (field: AdminSortField, label: string) => (
    <th className="px-3 py-2 text-left" aria-sort={ariaSort(field)}>
      <button type="button" onClick={() => toggleSort(field)} className="font-semibold hover:text-ink">
        {label}
        {sortIndicator(field)}
      </button>
    </th>
  );

  return (
    <div className="min-h-screen">
      <TopNav activeMode="admin" onSelect={handleModeNav} />
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-10">
        <h2 className="text-4xl font-bold mb-6 text-ink font-display">Users</h2>

        <form
          className="mb-4 flex gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            setPage(1);
            setSearch(searchInput.trim());
          }}
        >
          <input
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by email"
            aria-label="Search by email"
            className="rounded-lg px-4 py-2 text-lg font-body border border-border focus:border-accent focus:outline-none bg-white text-ink w-full max-w-sm"
          />
          <button
            type="submit"
            className="bg-primary text-white text-lg font-semibold font-body py-2 px-6 rounded-lg hover:bg-ink transition-colors duration-200"
          >
            Search
          </button>
        </form>

        {notice && (
          <div className="mb-4 text-lg font-body text-ink bg-lightbg border border-border rounded-lg p-3" role="status">
            {notice}
          </div>
        )}
        {error && <div className="mb-4 text-lg text-incorrect font-semibold font-body">{error}</div>}

        <div className="bg-white rounded-lg border border-border overflow-x-auto">
          <table className="w-full text-lg font-body text-ink">
            <thead className="bg-lightbg text-gray-500 text-base">
              <tr>
                <th className="px-3 py-2 text-left">Email</th>
                <th className="px-3 py-2 text-left">Country</th>
                {sortableHeader("payment_status", "Payment status")}
                <th className="px-3 py-2 text-left">Status</th>
                {sortableHeader("subscription_ends_at", "Subscription ends")}
                <th className="px-3 py-2 text-left">Actions</th>
              </tr>
            </thead>
            <tbody className={loading ? "opacity-50" : ""}>
              {rows.map((u) => {
                const active = isEffectivelyActive(u);
                return (
                  <tr key={u.id} className="border-t border-border">
                    <td className="px-3 py-2 break-all">{u.email}</td>
                    <td className="px-3 py-2">{u.country || "N/A"}</td>
                    <td className="px-3 py-2">{u.payment_status || "N/A"}</td>
                    <td className={`px-3 py-2 font-semibold ${active ? "text-success" : "text-incorrect"}`}>
                      {active ? "Active" : "Inactive"}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">{formatDate(u.subscription_ends_at)}</td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap gap-2">
                        {ADMIN_ACTIONS.map((label) => (
                          <button
                            key={label}
                            type="button"
                            onClick={() => setNotice(`"${label}" for ${u.email} is not available yet.`)}
                            className="text-base font-semibold font-body px-3 py-1 rounded-lg border border-border text-gray-600 hover:text-ink hover:border-accent transition-colors"
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-3 py-6 text-center text-gray-500">
                    No users found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="mt-4 flex items-center justify-between font-body text-lg text-ink">
          <span>
            {total} user{total === 1 ? "" : "s"} · Page {page} of {totalPages}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={page <= 1 || loading}
              onClick={() => setPage((p) => p - 1)}
              className="px-4 py-2 rounded-lg border border-border font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:border-accent"
            >
              Previous
            </button>
            <button
              type="button"
              disabled={page >= totalPages || loading}
              onClick={() => setPage((p) => p + 1)}
              className="px-4 py-2 rounded-lg border border-border font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:border-accent"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminUsers;
